import { formatBytes } from '@lib/utils/units'
import type {
  PublishProjectRequest,
  PublishResponse,
} from '@storeOld/reducers/publish'
import axios from 'axios'

import type { AttachmentItem, StorageLimits } from '@/types/models'
import type {
  AttachmentItemResponse,
  StorageLimitsResponse,
  TeamStorageLimitsResponse,
} from '@/types/responses'
import request from './request'

const mapAttachmentsResponseItem = (
  item: AttachmentItemResponse
): AttachmentItem => {
  const { id, filename, downloadUrl, createdAt, size: sizeString } = item

  const size = sizeString ? parseInt(sizeString, 10) : 0
  const hSize = formatBytes(size)

  return {
    id,
    filename,
    downloadUrl,
    createdAt,
    size,
    hSize,
  }
}

export const deleteProjectAttachment = async (id: string): Promise<void> => {
  return request.delete(`/attachments/${id}`)
}

export const fetchProjectAttachments = async (
  projectId?: string
): Promise<AttachmentItem[]> => {
  if (!projectId) {
    return []
  }

  const response = await request.get<AttachmentItemResponse[]>(
    `/projects/${projectId}/attachments`
  )

  return response.map(mapAttachmentsResponseItem)
}

const mapStorageLimits = ({
  max,
  usage,
}: StorageLimitsResponse): StorageLimits => {
  const hMax = formatBytes(max)
  const hUsage = formatBytes(usage)
  const percentageUsage = Math.round((100 * usage) / max)

  return {
    hMax,
    hUsage,
    max,
    usage,
    percentageUsage,
  }
}

export const fetchProjectStorageLimits = async (
  projectId?: string
): Promise<ProjectStorageLimits> => {
  const response = await request.get<ProjectStorageLimitsResponse>(
    `/projects/${projectId}/storage-usage`
  )

  // An older backend cannot supply the owner's total; do not invent a quota.
  const quota = response.quota ? mapStorageLimits(response.quota) : null

  return { limits: mapStorageLimits(response), quota }
}

export interface ProjectStorageLimits {
  limits: StorageLimits
  quota: StorageLimits | null
}

type ProjectStorageLimitsResponse = StorageLimitsResponse &
  Partial<{ quota: StorageLimitsResponse }>

export const fetchTeamProjectStorageLimits = async (
  teamId?: string
): Promise<StorageLimits> => {
  const response = await request.get<TeamStorageLimitsResponse>(
    `/my/teams/${teamId}/storage-usage`
  )

  const { usage, max } = Object.keys(response).reduce<StorageLimitsResponse>(
    (result, key) => {
      return {
        usage: result.usage + response[key].usage,
        max: response[key].max,
      }
    },
    { usage: 0, max: 0 }
  )

  return mapStorageLimits({ max, usage })
}

export const checkPassword = async ({
  projectId,
  password,
}: {
  password: string
  projectId: string
}): Promise<void> => {
  const formData = new FormData()
  formData.append('password', password)

  return request.urlencoded(`/projects/${projectId}/check-password`, formData)
}

export const getPublishProject = async (projectId: string) => {
  const { data } = await axios.get<PublishResponse | null>(
    `${import.meta.env.VITE_PUBLISH_HOST}/?projectId=${projectId}`
  )

  return data
}

export const publishProject = async (body: PublishProjectRequest) => {
  const { data } = await axios.post<PublishResponse>(
    `${import.meta.env.VITE_PUBLISH_HOST}/publish`,
    { ...body }
  )

  return data
}

export const deletePublishProject = async (projectId: string) => {
  const { data } = await axios.delete<{ message: string; isDeleted: boolean }>(
    `${import.meta.env.VITE_PUBLISH_HOST}/delete?projectId=${projectId}`
  )

  return data
}
